import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { formatIndianDate } from '../utils/formatters';
import { generateBillPDF, generateQuotationPDF, downloadPDF, previewPDF } from '../utils/pdfGenerator';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Search, FileText, Eye, Clock, CheckCircle, AlertTriangle, 
  IndianRupee, RefreshCw, Download, Plus, Calendar, Gift,
  FileSpreadsheet, Receipt, Building2, Phone, MapPin, Printer, Ban, XCircle
} from 'lucide-react';
import useProtectedExport from '../hooks/useProtectedExport';
import ExportPasswordDialog from '../components/ExportPasswordDialog';

const SalesBillingManager = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Data states
  const [awaitingBills, setAwaitingBills] = useState([]);
  const [completedPayments, setCompletedPayments] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [freeServices, setFreeServices] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [cancelledBills, setCancelledBills] = useState([]);
  const [stats, setStats] = useState({});
  const [businessInfo, setBusinessInfo] = useState(null);
  
  const [loading, setLoading] = useState(false);
  const protectedExport = useProtectedExport();
  const [activeTab, setActiveTab] = useState('awaiting');
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [billTypeFilter, setBillTypeFilter] = useState('all');
  const [overdueOnly, setOverdueOnly] = useState(false);
  
  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [dueDateDialogOpen, setDueDateDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelNotes, setCancelNotes] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [newDueDate, setNewDueDate] = useState('');
  const [dueDateNotes, setDueDateNotes] = useState('');

  // Role checks
  const isAdmin = user?.role === 'Admin';
  const isBackOfficeAccounts = user?.role === 'Backoffice Accounts Executive';
  const canAccess = isAdmin || isBackOfficeAccounts;

  // Fetch business info for PDF
  useEffect(() => {
    const fetchBusinessInfo = async () => {
      try {
        const response = await api.get('/business-settings/');
        setBusinessInfo(response.data);
      } catch (error) {
        // Use defaults if not available
        setBusinessInfo({
          business_name: 'SUNTECH ENTERPRISES',
          address: 'Coimbatore, Tamil Nadu'
        });
      }
    };
    fetchBusinessInfo();
  }, []);

  // PDF Generation handlers
  const handleDownloadBillPDF = async (bill) => {
    try {
      // Fetch full bill details if not already loaded
      let billData = bill;
      if (!bill.items) {
        const response = await api.get(`/sales-billing/bills/${bill.id}`);
        billData = response.data;
      }
      
      const doc = await generateBillPDF(billData, businessInfo);
      downloadPDF(doc, `${billData.bill_no}.pdf`);
      toast.success('Bill PDF downloaded');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const handlePrintBill = async (bill) => {
    try {
      let billData = bill;
      if (!bill.items) {
        const response = await api.get(`/sales-billing/bills/${bill.id}`);
        billData = response.data;
      }
      
      const doc = await generateBillPDF(billData, businessInfo);
      previewPDF(doc);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF for print');
    }
  };

  const handleDownloadQuotationPDF = async (quotation) => {
    try {
      let quotationData = quotation;
      if (!quotation.items) {
        const response = await api.get(`/sales-billing/quotations/${quotation.id}`);
        quotationData = response.data;
      }
      
      const doc = await generateQuotationPDF(quotationData, businessInfo);
      downloadPDF(doc, `${quotationData.quotation_no}.pdf`);
      toast.success('Quotation PDF downloaded');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const handlePrintQuotation = async (quotation) => {
    try {
      let quotationData = quotation;
      if (!quotation.items) {
        const response = await api.get(`/sales-billing/quotations/${quotation.id}`);
        quotationData = response.data;
      }
      
      const doc = await generateQuotationPDF(quotationData, businessInfo);
      previewPDF(doc);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF for print');
    }
  };

  // Fetch functions
  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/sales-billing/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const fetchAwaitingBills = useCallback(async () => {
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (searchTerm) params.search = searchTerm;
      
      const response = await api.get('/sales-billing/awaiting-bills', { params });
      setAwaitingBills(response.data);
    } catch (error) {
      console.error('Error fetching awaiting bills:', error);
    }
  }, [fromDate, toDate, searchTerm]);

  const fetchCompletedPayments = useCallback(async () => {
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (searchTerm) params.search = searchTerm;
      if (billTypeFilter !== 'all') params.bill_type = billTypeFilter;
      
      const response = await api.get('/sales-billing/completed-payments', { params });
      setCompletedPayments(response.data);
    } catch (error) {
      console.error('Error fetching completed payments:', error);
    }
  }, [fromDate, toDate, searchTerm, billTypeFilter]);

  const fetchPendingPayments = useCallback(async () => {
    try {
      const params = { overdue_only: overdueOnly };
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (searchTerm) params.search = searchTerm;
      
      const response = await api.get('/sales-billing/pending-payments', { params });
      setPendingPayments(response.data);
    } catch (error) {
      console.error('Error fetching pending payments:', error);
    }
  }, [fromDate, toDate, searchTerm, overdueOnly]);

  const fetchFreeServices = useCallback(async () => {
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (searchTerm) params.search = searchTerm;
      
      const response = await api.get('/sales-billing/free-services', { params });
      setFreeServices(response.data);
    } catch (error) {
      console.error('Error fetching free services:', error);
    }
  }, [fromDate, toDate, searchTerm]);

  const fetchQuotations = useCallback(async () => {
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (searchTerm) params.search = searchTerm;
      
      const response = await api.get('/sales-billing/quotations', { params });
      setQuotations(response.data);
    } catch (error) {
      console.error('Error fetching quotations:', error);
    }
  }, [fromDate, toDate, searchTerm]);

  const fetchCancelledBills = useCallback(async () => {
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (searchTerm) params.search = searchTerm;
      
      const response = await api.get('/sales-billing/cancelled-bills', { params });
      setCancelledBills(response.data);
    } catch (error) {
      console.error('Error fetching cancelled bills:', error);
    }
  }, [fromDate, toDate, searchTerm]);

  // Fetch data based on active tab
  useEffect(() => {
    if (!canAccess) return;
    
    fetchStats();
    
    switch (activeTab) {
      case 'awaiting':
        fetchAwaitingBills();
        break;
      case 'completed':
        fetchCompletedPayments();
        break;
      case 'pending':
        fetchPendingPayments();
        break;
      case 'free':
        fetchFreeServices();
        break;
      case 'quotations':
        fetchQuotations();
        break;
      case 'cancelled':
        fetchCancelledBills();
        break;
      default:
        fetchAwaitingBills();
    }
  }, [canAccess, activeTab, fetchStats, fetchAwaitingBills, fetchCompletedPayments, fetchPendingPayments, fetchFreeServices, fetchQuotations, fetchCancelledBills]);

  const handleRefresh = () => {
    setLoading(true);
    fetchStats();
    switch (activeTab) {
      case 'awaiting': fetchAwaitingBills(); break;
      case 'completed': fetchCompletedPayments(); break;
      case 'pending': fetchPendingPayments(); break;
      case 'free': fetchFreeServices(); break;
      case 'quotations': fetchQuotations(); break;
      case 'cancelled': fetchCancelledBills(); break;
      default: fetchAwaitingBills();
    }
    setTimeout(() => setLoading(false), 500);
  };

  const handleExport = async (format = 'csv') => {
    try {
      const divisionMap = {
        'awaiting': 'awaiting-bills',
        'completed': 'completed',
        'pending': 'pending',
        'free': 'free',
        'quotations': 'quotations'
      };
      
      const params = { format };
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      
      const response = await api.get(`/sales-billing/export/${divisionMap[activeTab]}`, {
        params,
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${divisionMap[activeTab]}_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded successfully');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const handleViewBill = async (bill) => {
    try {
      const response = await api.get(`/sales-billing/bills/${bill.id}`);
      setSelectedItem(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load bill details');
    }
  };

  const handleViewQuotation = async (quotation) => {
    try {
      const response = await api.get(`/sales-billing/quotations/${quotation.id}`);
      setSelectedItem(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load quotation details');
    }
  };

  const openDueDateDialog = (bill) => {
    setSelectedItem(bill);
    setNewDueDate(bill.due_date || '');
    setDueDateNotes('');
    setDueDateDialogOpen(true);
  };

  const handleUpdateDueDate = async () => {
    if (!dueDateNotes || dueDateNotes.trim().length < 3) {
      toast.error('Notes are mandatory (minimum 3 characters)');
      return;
    }

    setLoading(true);
    try {
      await api.put(`/sales-billing/bills/${selectedItem.id}/due-date`, {
        due_date: newDueDate,
        notes: dueDateNotes.trim()
      });
      toast.success('Due date updated successfully');
      setDueDateDialogOpen(false);
      fetchPendingPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update due date');
    } finally {
      setLoading(false);
    }
  };

  const openCancelDialog = (bill) => {
    setSelectedItem(bill);
    setCancelNotes('');
    setCancelDialogOpen(true);
  };

  const handleCancelBill = async () => {
    if (!cancelNotes || cancelNotes.trim().length < 5) {
      toast.error('Cancellation notes are mandatory (minimum 5 characters)');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/sales-billing/bills/${selectedItem.id}/cancel`, {
        cancellation_notes: cancelNotes.trim()
      });
      toast.success(`Bill ${selectedItem.bill_no} cancelled successfully`);
      setCancelDialogOpen(false);
      setCancelNotes('');
      fetchStats();
      // Refresh current tab
      if (activeTab === 'pending') fetchPendingPayments();
      else if (activeTab === 'completed') fetchCompletedPayments();
      fetchCancelledBills();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel bill');
    } finally {
      setLoading(false);
    }
  };

  const navigateToCreateBill = (service = null) => {
    if (service) {
      navigate('/sales-billing/create-bill', { state: { service } });
    } else {
      navigate('/sales-billing/create-bill');
    }
  };

  const navigateToCreateQuotation = () => {
    navigate('/sales-billing/create-quotation');
  };

  if (!canAccess) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="w-96">
          <CardHeader>
            <CardTitle className="text-red-600">Access Denied</CardTitle>
            <CardDescription>
              Only Admin and Backoffice Accounts Executive can access Sales & Billing.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sales-billing-manager">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Sales & Billing</h1>
          <p className="text-gray-600 mt-1">Manage bills, payments, and quotations</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleRefresh} disabled={loading} variant="outline">
            <RefreshCw size={18} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => navigateToCreateBill()} className="bg-blue-600 hover:bg-blue-700">
            <Plus size={18} className="mr-2" />
            New Bill
          </Button>
          <Button onClick={navigateToCreateQuotation} variant="outline">
            <FileText size={18} className="mr-2" />
            New Quotation
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className={`grid grid-cols-1 ${isAdmin ? 'md:grid-cols-6' : 'md:grid-cols-5'} gap-4`}>
        <Card className="bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-200 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setActiveTab('awaiting')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-600">Bills to Generate</p>
                <p className="text-3xl font-bold text-yellow-800">{stats.awaiting_bills_count || 0}</p>
              </div>
              <Receipt className="h-10 w-10 text-yellow-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-green-50 to-green-100 border-green-200 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setActiveTab('completed')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Completed</p>
                <p className="text-3xl font-bold text-green-800">₹{(stats.completed_payments_total || 0).toLocaleString()}</p>
                <p className="text-xs text-green-600">{stats.completed_payments_count || 0} bills</p>
              </div>
              <CheckCircle className="h-10 w-10 text-green-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-orange-50 to-orange-100 border-orange-200 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setActiveTab('pending')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600">Pending</p>
                <p className="text-3xl font-bold text-orange-800">₹{(stats.pending_payments_total || 0).toLocaleString()}</p>
                <p className="text-xs text-orange-600">{stats.pending_payments_count || 0} bills</p>
              </div>
              <Clock className="h-10 w-10 text-orange-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className={`bg-gradient-to-r from-red-50 to-red-100 border-red-200 cursor-pointer hover:shadow-md transition-shadow ${stats.overdue_count > 0 ? 'ring-2 ring-red-400' : ''}`}
              onClick={() => { setActiveTab('pending'); setOverdueOnly(true); }}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-600">Overdue</p>
                <p className="text-3xl font-bold text-red-800">₹{(stats.overdue_total || 0).toLocaleString()}</p>
                <p className="text-xs text-red-600">{stats.overdue_count || 0} bills</p>
              </div>
              <AlertTriangle className="h-10 w-10 text-red-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-purple-50 to-purple-100 border-purple-200 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setActiveTab('free')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">Free Services</p>
                <p className="text-3xl font-bold text-purple-800">{stats.free_services_count || 0}</p>
              </div>
              <Gift className="h-10 w-10 text-purple-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        {isAdmin && (
          <Card className="bg-gradient-to-r from-gray-50 to-gray-100 border-gray-300 cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => setActiveTab('cancelled')}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Cancelled</p>
                  <p className="text-3xl font-bold text-gray-800">₹{(stats.cancelled_total || 0).toLocaleString()}</p>
                  <p className="text-xs text-gray-600">{stats.cancelled_count || 0} invoices</p>
                </div>
                <Ban className="h-10 w-10 text-gray-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setOverdueOnly(false); }}>
        <TabsList className={`grid w-full ${isAdmin ? 'max-w-4xl grid-cols-6' : 'max-w-3xl grid-cols-5'}`}>
          <TabsTrigger value="awaiting" className="flex items-center gap-1">
            <Receipt size={14} />
            Bills to Generate
            {stats.awaiting_bills_count > 0 && (
              <Badge variant="secondary" className="ml-1">{stats.awaiting_bills_count}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="completed" className="flex items-center gap-1">
            <CheckCircle size={14} />
            Completed
          </TabsTrigger>
          <TabsTrigger value="pending" className="flex items-center gap-1">
            <Clock size={14} />
            Pending
            {stats.overdue_count > 0 && (
              <Badge variant="destructive" className="ml-1">{stats.overdue_count}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="free" className="flex items-center gap-1">
            <Gift size={14} />
            Free Services
          </TabsTrigger>
          <TabsTrigger value="quotations" className="flex items-center gap-1">
            <FileText size={14} />
            Quotations
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="cancelled" className="flex items-center gap-1" data-testid="tab-cancelled">
              <Ban size={14} />
              Cancelled
              {stats.cancelled_count > 0 && (
                <Badge variant="secondary" className="ml-1 bg-red-100 text-red-700">{stats.cancelled_count}</Badge>
              )}
            </TabsTrigger>
          )}
        </TabsList>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 my-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                placeholder="Search by ID, customer, mobile..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <Calendar size={18} className="text-gray-400" />
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-40"
              data-testid="from-date"
            />
            <span className="text-gray-400">to</span>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-40"
              data-testid="to-date"
            />
          </div>
          {activeTab === 'completed' && (
            <Select value={billTypeFilter} onValueChange={setBillTypeFilter}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Bill Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Bills</SelectItem>
                <SelectItem value="GST">GST Bills</SelectItem>
                <SelectItem value="Cash">Cash Bills</SelectItem>
              </SelectContent>
            </Select>
          )}
          {activeTab === 'pending' && (
            <Button
              variant={overdueOnly ? 'default' : 'outline'}
              size="sm"
              onClick={() => setOverdueOnly(!overdueOnly)}
              className={overdueOnly ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              <AlertTriangle size={14} className="mr-1" />
              Overdue Only
            </Button>
          )}
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => protectedExport.requestExport(() => handleExport('csv'))} data-testid="sales-billing-export-csv">
              <Download size={14} className="mr-1" />
              Export CSV
            </Button>
          )}
        </div>

        {/* Bills to Generate Tab */}
        <TabsContent value="awaiting">
          <Card>
            <CardHeader>
              <CardTitle>Services Awaiting Bill Generation</CardTitle>
              <CardDescription>
                Approved services that need Cash Bill or GST Bill to be generated
              </CardDescription>
            </CardHeader>
            <CardContent>
              {awaitingBills.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Receipt className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No services awaiting bill generation</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Service #</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Mobile</TableHead>
                        <TableHead>City</TableHead>
                        <TableHead>Engineer</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Payment</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {awaitingBills.map((service) => (
                        <TableRow key={service.id} data-testid={`awaiting-${service.id}`}>
                          <TableCell className="whitespace-nowrap">
                            {service.closed_at ? formatIndianDate(service.closed_at) : '-'}
                          </TableCell>
                          <TableCell className="font-medium">{service.service_no}</TableCell>
                          <TableCell>{service.customer_name}</TableCell>
                          <TableCell>{service.customer_mobile}</TableCell>
                          <TableCell>{service.customer_city}</TableCell>
                          <TableCell>{service.engineer_name}</TableCell>
                          <TableCell className="font-semibold text-green-700">
                            ₹{(service.service_charge || 0).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <Badge className={service.payment_status === 'Paid' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}>
                              {service.payment_status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700"
                                onClick={() => navigateToCreateBill(service)}
                                data-testid={`generate-bill-${service.id}`}
                              >
                                <Receipt size={14} className="mr-1" />
                                Generate Bill
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Completed Payments Tab */}
        <TabsContent value="completed">
          <Card>
            <CardHeader>
              <CardTitle>Completed Payments</CardTitle>
              <CardDescription>
                Bills that have been fully paid
              </CardDescription>
            </CardHeader>
            <CardContent>
              {completedPayments.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-300" />
                  <p>No completed payments yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Bill #</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Mobile</TableHead>
                        <TableHead>City</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {completedPayments.map((bill) => (
                        <TableRow key={bill.id} data-testid={`completed-${bill.id}`}>
                          <TableCell className="whitespace-nowrap">
                            {bill.bill_date}
                          </TableCell>
                          <TableCell className="font-medium">{bill.bill_no}</TableCell>
                          <TableCell>
                            <Badge className={bill.bill_type === 'GST' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}>
                              {bill.bill_type}
                            </Badge>
                          </TableCell>
                          <TableCell>{bill.customer_name}</TableCell>
                          <TableCell>{bill.customer_mobile}</TableCell>
                          <TableCell>{bill.customer_city}</TableCell>
                          <TableCell className="font-semibold text-green-700">
                            ₹{(bill.net_amount || 0).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewBill(bill)}
                              >
                                <Eye size={14} className="mr-1" />
                                View
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDownloadBillPDF(bill)}
                              >
                                <Download size={14} className="mr-1" />
                                PDF
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handlePrintBill(bill)}
                              >
                                <Printer size={14} className="mr-1" />
                                Print
                              </Button>
                              {isAdmin && (
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => openCancelDialog(bill)}
                                  data-testid={`cancel-completed-${bill.id}`}
                                >
                                  <Ban size={14} className="mr-1" />
                                  Cancel
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Payments Tab */}
        <TabsContent value="pending">
          <Card>
            <CardHeader>
              <CardTitle>Pending Payments</CardTitle>
              <CardDescription>
                Bills with pending or partial payments - {overdueOnly && 'Showing overdue only'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {pendingPayments.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Clock className="h-12 w-12 mx-auto mb-4 text-orange-300" />
                  <p>{overdueOnly ? 'No overdue payments' : 'No pending payments'}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Bill #</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead>Paid</TableHead>
                        <TableHead>Pending</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingPayments.map((bill) => (
                        <TableRow 
                          key={bill.id} 
                          className={bill.is_overdue ? 'bg-red-50' : ''}
                          data-testid={`pending-${bill.id}`}
                        >
                          <TableCell className="whitespace-nowrap">
                            {bill.bill_date}
                          </TableCell>
                          <TableCell className="font-medium">{bill.bill_no}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{bill.customer_name}</p>
                              <p className="text-xs text-gray-500">{bill.customer_mobile}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className={bill.is_overdue ? 'text-red-600 font-semibold' : ''}>
                              {bill.due_date || '-'}
                            </span>
                          </TableCell>
                          <TableCell>₹{(bill.net_amount || 0).toLocaleString()}</TableCell>
                          <TableCell className="text-green-600">₹{(bill.amount_paid || 0).toLocaleString()}</TableCell>
                          <TableCell className="font-semibold text-orange-600">
                            ₹{(bill.pending_amount || 0).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            {bill.is_overdue ? (
                              <Badge variant="destructive">Overdue</Badge>
                            ) : (
                              <Badge className="bg-orange-100 text-orange-800">
                                {bill.payment_status}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewBill(bill)}
                              >
                                <Eye size={14} />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openDueDateDialog(bill)}
                              >
                                <Calendar size={14} className="mr-1" />
                                Edit Due
                              </Button>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => navigate('/payment-collections', { state: { bill } })}
                              >
                                <IndianRupee size={14} className="mr-1" />
                                Collect
                              </Button>
                              {isAdmin && (
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => openCancelDialog(bill)}
                                  data-testid={`cancel-pending-${bill.id}`}
                                >
                                  <Ban size={14} className="mr-1" />
                                  Cancel
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Free Services Tab */}
        <TabsContent value="free">
          <Card>
            <CardHeader>
              <CardTitle>Free Services Completed</CardTitle>
              <CardDescription>
                All services completed as free (latest first)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {freeServices.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Gift className="h-12 w-12 mx-auto mb-4 text-purple-300" />
                  <p>No free services completed</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Service #</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Mobile</TableHead>
                        <TableHead>City</TableHead>
                        <TableHead>Engineer</TableHead>
                        <TableHead>Service Type</TableHead>
                        <TableHead>Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {freeServices.map((service) => (
                        <TableRow key={service.id} data-testid={`free-${service.id}`}>
                          <TableCell className="whitespace-nowrap">
                            {service.closed_at ? formatIndianDate(service.closed_at) : '-'}
                          </TableCell>
                          <TableCell className="font-medium">{service.service_no}</TableCell>
                          <TableCell>{service.customer_name}</TableCell>
                          <TableCell>{service.customer_mobile}</TableCell>
                          <TableCell>{service.customer_city}</TableCell>
                          <TableCell>{service.engineer_name}</TableCell>
                          <TableCell>{service.service_type}</TableCell>
                          <TableCell className="max-w-xs truncate" title={service.free_service_notes}>
                            {service.free_service_notes || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Quotations Tab */}
        <TabsContent value="quotations">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Quotations</CardTitle>
                <CardDescription>
                  All quotations (latest first)
                </CardDescription>
              </div>
              <Button onClick={navigateToCreateQuotation} size="sm">
                <Plus size={14} className="mr-1" />
                New Quotation
              </Button>
            </CardHeader>
            <CardContent>
              {quotations.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No quotations created</p>
                  <Button onClick={navigateToCreateQuotation} className="mt-4" variant="outline">
                    <Plus size={14} className="mr-1" />
                    Create First Quotation
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Quote #</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Mobile</TableHead>
                        <TableHead>City</TableHead>
                        <TableHead>Valid Until</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {quotations.map((quotation) => (
                        <TableRow key={quotation.id} data-testid={`quotation-${quotation.id}`}>
                          <TableCell className="whitespace-nowrap">
                            {quotation.quotation_date}
                          </TableCell>
                          <TableCell className="font-medium">{quotation.quotation_no}</TableCell>
                          <TableCell>{quotation.customer_name}</TableCell>
                          <TableCell>{quotation.customer_mobile}</TableCell>
                          <TableCell>{quotation.customer_city}</TableCell>
                          <TableCell>{quotation.valid_until || '-'}</TableCell>
                          <TableCell className="font-semibold">
                            ₹{(quotation.net_amount || 0).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewQuotation(quotation)}
                              >
                                <Eye size={14} className="mr-1" />
                                View
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDownloadQuotationPDF(quotation)}
                              >
                                <Download size={14} className="mr-1" />
                                PDF
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handlePrintQuotation(quotation)}
                              >
                                <Printer size={14} className="mr-1" />
                                Print
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cancelled Bills Tab (Admin only) */}
        {isAdmin && (
          <TabsContent value="cancelled">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Ban size={18} className="text-red-600" />
                  Cancelled Invoices
                </CardTitle>
                <CardDescription>
                  Bills cancelled with reason. Ledger entries reversed automatically.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {cancelledBills.length === 0 ? (
                  <div className="text-center py-12 text-gray-500" data-testid="cancelled-empty">
                    <Ban className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No cancelled invoices</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Cancelled On</TableHead>
                          <TableHead>Bill #</TableHead>
                          <TableHead>Bill Date</TableHead>
                          <TableHead>Customer</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Cancelled By</TableHead>
                          <TableHead>Notes</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {cancelledBills.map((bill) => (
                          <TableRow key={bill.id} data-testid={`cancelled-${bill.id}`}>
                            <TableCell className="whitespace-nowrap text-sm">
                              {bill.cancelled_at ? formatIndianDate(bill.cancelled_at) : '-'}
                            </TableCell>
                            <TableCell className="font-medium line-through text-gray-600">{bill.bill_no}</TableCell>
                            <TableCell>{bill.bill_date}</TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{bill.customer_name}</p>
                                <p className="text-xs text-gray-500">{bill.customer_mobile}</p>
                              </div>
                            </TableCell>
                            <TableCell className="font-semibold text-red-700">
                              ₹{(bill.net_amount || 0).toLocaleString()}
                            </TableCell>
                            <TableCell className="text-sm">{bill.cancelled_by_name || '-'}</TableCell>
                            <TableCell className="max-w-xs">
                              <span className="text-sm text-gray-700" title={bill.cancellation_notes}>
                                {bill.cancellation_notes || '-'}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewBill(bill)}
                              >
                                <Eye size={14} className="mr-1" />
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* View Bill/Quotation Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedItem?.bill_no ? `Bill - ${selectedItem.bill_no}` : `Quotation - ${selectedItem?.quotation_no}`}
            </DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-6">
              {/* Customer Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Building2 size={18} /> Customer Information
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Name:</span>
                    <span className="ml-2 font-medium">{selectedItem.customer?.company_name}</span>
                  </div>
                  <div className="flex items-center">
                    <Phone size={14} className="text-gray-400 mr-1" />
                    <span>{selectedItem.customer?.mobile}</span>
                  </div>
                  <div className="flex items-center">
                    <MapPin size={14} className="text-gray-400 mr-1" />
                    <span>{selectedItem.customer?.city}</span>
                  </div>
                  {selectedItem.customer?.gst_number && (
                    <div>
                      <span className="text-gray-500">GSTIN:</span>
                      <span className="ml-2">{selectedItem.customer?.gst_number}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Items Table */}
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Items</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Description</TableHead>
                      <TableHead>HSN</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      {selectedItem.is_igst ? (
                        <TableHead className="text-right">IGST</TableHead>
                      ) : (
                        <>
                          <TableHead className="text-right">CGST</TableHead>
                          <TableHead className="text-right">SGST</TableHead>
                        </>
                      )}
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedItem.items?.map((item, index) => (
                      <React.Fragment key={index}>
                        <TableRow>
                          <TableCell>{item.description}</TableCell>
                          <TableCell>{item.hsn_code || '-'}</TableCell>
                          <TableCell className="text-right">{item.quantity}</TableCell>
                          <TableCell className="text-right">₹{item.unit_price?.toLocaleString()}</TableCell>
                          {selectedItem.is_igst ? (
                            <TableCell className="text-right">
                              {item.igst_percent}% (₹{item.igst_amount?.toLocaleString()})
                            </TableCell>
                          ) : (
                            <>
                              <TableCell className="text-right">
                                {item.cgst_percent}% (₹{item.cgst_amount?.toLocaleString()})
                              </TableCell>
                              <TableCell className="text-right">
                                {item.sgst_percent}% (₹{item.sgst_amount?.toLocaleString()})
                              </TableCell>
                            </>
                          )}
                          <TableCell className="text-right font-medium">₹{item.total?.toLocaleString()}</TableCell>
                        </TableRow>
                        {item.selected_serials && item.selected_serials.length > 0 && (
                          <TableRow className="bg-gray-50/50">
                            <TableCell colSpan={selectedItem.is_igst ? 6 : 7} className="py-1 px-4">
                              <span className="text-xs text-gray-500 mr-2">Serial No:</span>
                              {item.selected_serials.map((sn, snIdx) => (
                                <Badge key={snIdx} variant="outline" className="mr-1 text-xs font-mono" data-testid={`bill-serial-${sn}`}>
                                  {sn}
                                </Badge>
                              ))}
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Totals */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-right text-gray-600">Subtotal:</div>
                  <div className="text-right">₹{selectedItem.subtotal?.toLocaleString()}</div>
                  
                  {selectedItem.total_discount > 0 && (
                    <>
                      <div className="text-right text-gray-600">Discount:</div>
                      <div className="text-right text-red-600">-₹{selectedItem.total_discount?.toLocaleString()}</div>
                    </>
                  )}
                  
                  {selectedItem.is_igst ? (
                    <>
                      <div className="text-right text-gray-600">IGST:</div>
                      <div className="text-right">₹{selectedItem.total_igst?.toLocaleString()}</div>
                    </>
                  ) : (
                    <>
                      <div className="text-right text-gray-600">CGST:</div>
                      <div className="text-right">₹{selectedItem.total_cgst?.toLocaleString()}</div>
                      
                      <div className="text-right text-gray-600">SGST:</div>
                      <div className="text-right">₹{selectedItem.total_sgst?.toLocaleString()}</div>
                    </>
                  )}
                  
                  <div className="text-right font-semibold text-lg border-t pt-2">Net Amount:</div>
                  <div className="text-right font-bold text-lg border-t pt-2">₹{selectedItem.net_amount?.toLocaleString()}</div>
                </div>
              </div>

              {/* Payment History (for bills) */}
              {selectedItem.payment_history && selectedItem.payment_history.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-700 mb-3">Payment History</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Collection #</TableHead>
                        <TableHead>Mode</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedItem.payment_history.map((payment, index) => (
                        <TableRow key={index}>
                          <TableCell>{payment.payment_date}</TableCell>
                          <TableCell>{payment.collection_no}</TableCell>
                          <TableCell>{payment.payment_mode}</TableCell>
                          <TableCell className="text-right text-green-600">₹{payment.amount?.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
            <Button 
              variant="outline"
              onClick={() => {
                if (selectedItem?.bill_no) {
                  handleDownloadBillPDF(selectedItem);
                } else {
                  handleDownloadQuotationPDF(selectedItem);
                }
              }}
            >
              <Download size={16} className="mr-2" />
              Download PDF
            </Button>
            <Button 
              onClick={() => {
                if (selectedItem?.bill_no) {
                  handlePrintBill(selectedItem);
                } else {
                  handlePrintQuotation(selectedItem);
                }
              }}
            >
              <Printer size={16} className="mr-2" />
              Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Due Date Dialog */}
      <Dialog open={dueDateDialogOpen} onOpenChange={setDueDateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Due Date</DialogTitle>
            <DialogDescription>
              Update payment due date for {selectedItem?.bill_no}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Due Date *</Label>
              <Input
                type="date"
                value={newDueDate}
                onChange={(e) => setNewDueDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="space-y-2">
              <Label>Notes * (Reason for change)</Label>
              <Textarea
                value={dueDateNotes}
                onChange={(e) => setDueDateNotes(e.target.value)}
                placeholder="Enter reason for due date change..."
                rows={3}
              />
              <p className="text-xs text-gray-500">Notes are mandatory (minimum 3 characters)</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDueDateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateDueDate}
              disabled={loading || !newDueDate || !dueDateNotes || dueDateNotes.trim().length < 3}
            >
              {loading ? 'Updating...' : 'Update Due Date'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Cancel Bill Dialog (Admin only) */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent data-testid="cancel-bill-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle size={20} />
              Cancel Invoice {selectedItem?.bill_no}
            </DialogTitle>
            <DialogDescription className="text-red-600">
              <strong>Warning:</strong> This invoice will be cancelled. The customer ledger entries
              will be reversed automatically. Any partial payments already collected will be retained
              as customer advance/credit (visible on Payment Collections). Inventory & serial numbers
              will be restored. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm space-y-1">
              <div><span className="text-gray-600">Bill Amount:</span> <span className="font-semibold ml-2">₹{(selectedItem?.net_amount || 0).toLocaleString()}</span></div>
              {selectedItem?.amount_paid > 0 && (
                <div><span className="text-gray-600">Already Paid (kept as advance):</span> <span className="font-semibold text-green-700 ml-2">₹{(selectedItem?.amount_paid || 0).toLocaleString()}</span></div>
              )}
              <div><span className="text-gray-600">Customer:</span> <span className="font-medium ml-2">{selectedItem?.customer_name}</span></div>
            </div>
            <div className="space-y-2">
              <Label>Cancellation Notes * <span className="text-xs text-gray-500">(minimum 5 characters)</span></Label>
              <Textarea
                value={cancelNotes}
                onChange={(e) => setCancelNotes(e.target.value)}
                placeholder="Reason for cancellation (mandatory)..."
                rows={4}
                data-testid="cancel-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>
              Keep Invoice
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelBill}
              disabled={loading || !cancelNotes || cancelNotes.trim().length < 5}
              data-testid="confirm-cancel-bill"
            >
              <XCircle size={16} className="mr-2" />
              {loading ? 'Cancelling...' : 'Confirm Cancel Invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ExportPasswordDialog {...protectedExport} />
    </div>
  );
};

export default SalesBillingManager;
